import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { LIST_LIMITS } from "@estelle/domain";
import type {
  DataSource,
  DocFolderWithAccess,
  DocPage,
  DocPageWithContent,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

export interface DocsTree {
  /** Every folder the caller can read (FR-DOC-9 already filtered this). */
  folders: DocFolderWithAccess[];
  pagesByFolder: Map<string, DocPage[]>;
  allPages: DocPage[];
  /** Each page's current revision, keyed by page id. */
  contentByPageId: Map<string, DocPageWithContent>;
}

/**
 * The whole visible handbook: every readable folder, every page in each
 * one, and each page's current content.
 *
 * `DocsRepository` has `listPages(folderId)` and `getPage(id)`, but nothing
 * that returns "every page, with content, across every folder" in one call.
 * Browse, Recent and Search all need exactly that shape, so it is walked
 * once here and shared rather than three times — the same bounded
 * client-side join `modules/orders/localQuery.ts` documents for order
 * history, and bounded the same way: fine against a handbook-sized tree,
 * not a pattern that would survive an unbounded one.
 *
 * A plain async function, not just a hook, so `index.ts`'s CSV export for
 * Recent — which runs outside any component — can call the exact same walk
 * rather than a second copy of it.
 */
export async function fetchDocsTree(data: DataSource): Promise<DocsTree> {
  const folders = await data.docs.listFolders();

  const folderEntries = await Promise.all(
    folders.map(async (folder) => {
      const page = await data.docs.listPages(folder.id, {
        limit: LIST_LIMITS.docs.max,
      });
      return [folder.id, page.items] as const;
    }),
  );
  const pagesByFolder = new Map(folderEntries);
  const allPages = folderEntries.flatMap(([, pages]) => pages);

  const contentEntries = await Promise.all(
    allPages.map(async (page) => {
      const content = await data.docs.getPage(page.id);
      return [page.id, content] as const;
    }),
  );
  const contentByPageId = new Map(
    contentEntries.filter(
      (entry): entry is [string, DocPageWithContent] => entry[1] !== null,
    ),
  );

  return { folders, pagesByFolder, allPages, contentByPageId };
}

export function useDocsTree(): UseQueryResult<DocsTree> {
  const data = useDataSource();

  return useQuery({
    queryKey: ["docs", "tree"],
    queryFn: () => fetchDocsTree(data),
  });
}
